import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const ITEMS = [
  { to: "/sobre", label: "Sobre" },
  { to: "/", hash: "features", label: "Funcionalidades" },
  { to: "/blog", label: "Blog" },
  { to: "/", hash: "integrations", label: "Integrações" },
  { to: "/", hash: "pricing", label: "Preços" },
  { to: "/", hash: "faq", label: "FAQ" },
];

/**
 * Public-site responsive header used on Landing, About, Blog, BlogPost, Cases.
 * Desktop (lg+): horizontal nav.
 * Mobile/Tablet (< lg): hamburger that opens a full-screen drawer.
 */
export default function PublicHeader({ active }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <header className="relative z-30 border-b border-border/50 bg-[#050B14]/80 backdrop-blur-xl sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(true)}
            aria-label="Abrir menu"
            className="lg:hidden p-2 -ml-2 rounded-md hover:bg-secondary/50 transition shrink-0"
          >
            <Menu className="w-5 h-5" />
          </button>

          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center pulse-coral">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">SmartStockAI</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 text-sm text-muted-foreground flex-1 justify-center ml-4">
            {ITEMS.map((it) => {
              const isActive = active === it.label.toLowerCase();
              const cls = isActive ? "text-foreground" : "hover:text-foreground transition";
              const to = it.hash ? `${it.to}#${it.hash}` : it.to;
              return (
                <Link key={it.label} to={to} className={cls}>{it.label}</Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 shrink-0 ml-auto">
            <Link to="/login" className="hidden sm:inline-block">
              <Button data-testid="nav-login-btn" variant="ghost" size="sm">Entrar</Button>
            </Link>
            <Link to="/signup">
              <Button
                data-testid="nav-signup-btn"
                size="sm"
                className="bg-primary hover:bg-primary/90 rounded-full pl-3 pr-4 shadow-[0_6px_20px_-6px_rgba(240,90,63,0.7)] hover:shadow-[0_10px_28px_-8px_rgba(240,90,63,0.9)] transition-all"
              >
                <span className="text-base mr-1.5 inline-block transition-transform group-hover:-rotate-12">🚀</span>
                <span className="hidden sm:inline font-semibold">Testar Grátis</span>
                <span className="sm:hidden font-semibold">Grátis</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div data-testid="mobile-menu-drawer" className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute top-0 left-0 bottom-0 w-full sm:w-[420px] bg-[#070F1B] border-r border-border flex flex-col fade-up">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
                <span className="font-display font-bold text-lg">SmartStockAI</span>
              </Link>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Fechar menu" className="p-2 rounded-md hover:bg-secondary/50">
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4">
              {ITEMS.map((it) => {
                const to = it.hash ? `${it.to}#${it.hash}` : it.to;
                return (
                  <Link
                    key={it.label}
                    to={to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${it.label.toLowerCase()}`}
                    className="flex items-center justify-between px-4 py-4 rounded-lg text-base font-medium hover:bg-primary/10 hover:text-primary transition border border-transparent hover:border-primary/30"
                  >
                    <span>{it.label}</span>
                    <ArrowRight className="w-4 h-4 opacity-40" />
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}
    </>
  );
}
